import { useState, useEffect, useMemo } from "react";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./DettaglioAttivi.css";
import "../../../../src/index.css";
import Lf23Loader from "../../../components/Lf23Loader";

const DettaglioAttivi = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [tableData, setTableData] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(true);
    const [cardsData, setCardsData] = useState([]);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const sortedTableData = useMemo(() => {
        if (!sortConfig.key) return tableData;
        const toComparable = (val) => {
            if (val === null || val === undefined) return '';
            const s = String(val);
            // Converte DD/MM/YYYY → YYYY-MM-DD per ordinamento corretto
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
                const [d, m, y] = s.split('/');
                return `${y}-${m}-${d}`;
            }
            const num = parseFloat(s);
            return !isNaN(num) ? num : s;
        };
        return [...tableData].sort((a, b) => {
            const ca = toComparable(a[sortConfig.key]);
            const cb = toComparable(b[sortConfig.key]);
            const cmp = typeof ca === 'number' && typeof cb === 'number'
                ? ca - cb
                : String(ca).localeCompare(String(cb));
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [tableData, sortConfig]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenTable(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    useEffect(() => {
        const fetchData = async () => {
            setDataLoading(true);
            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    ccnl: filters.ccnl?.map(c => c.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || ""
                };
                const res = await axios.get(`${API_URL}/hr-generale/dettaglio-attivi`, { params });
                setTableData(res.data.tableData || []);
                setCardsData(res.data.cardsData || []);
            } catch (error) {
                console.error("Errore nel caricamento Dettaglio Attivi:", error);
            } finally {
                setDataLoading(false);
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters]);

    const isLoading = parentLoading || dataLoading || filterLoading;

    const getCardData = (tipo, annoIndex) => {
        const fromYear = new Date(filters.fromDate).getFullYear();
        const toYear = new Date(filters.toDate).getFullYear();
        const anno1 = fromYear !== toYear ? fromYear : fromYear - 1;
        const anno2 = fromYear !== toYear ? toYear : fromYear;
        const anno = annoIndex === 0 ? anno1 : anno2;
        const found = cardsData.find(c => c.tipo === tipo && c.anno === anno);
        const totale = found ? parseInt(found.totale) : 0;
        const svantaggiati = found ? parseInt(found.svantaggiati) : 0;
        const nonSvantaggiati = totale - svantaggiati;
        const percentuale = nonSvantaggiati > 0
            ? ((svantaggiati / nonSvantaggiati) * 100).toFixed(2)
            : '0.00';
        return { anno, totale, svantaggiati, percentuale };
    };

    const prepareExcelData = () => {
        const headers = [
            "Operatore",
            "Codice Filiale",
            "Età",
            "Sesso",
            "Svantaggio",
            "Data Inizio",
            "Data Fine",
            "Tipologia Contratto",
            "Dett Contratto",
            "Livello",
            "CCNL"
        ];

        const rows = sortedTableData.map(row => [
            row.operatore || "-",
            row.codice_filiale || "-",
            row.eta || "-",
            row.sesso || "-",
            row.svantaggio || "-",
            row.data_inizio || "-",
            row.data_fine || "-",
            row.rapporto_lavoro || "-",
            row.tipologia || "-",
            row.livello || "-",
            row.ccnl || "-"
        ]);

        return { headers, rows };
    };

    const handleExportExcel = async () => {
        try {
            const excelData = prepareExcelData();

            const response = await axios.post(
                `${API_URL}/hr-generale/downloadExcel`,
                {
                    tableName: "Dettaglio Attivi",
                    datiDaStampare: excelData
                },
                { responseType: "blob" }
            );

            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", "dettaglio_attivi.xlsx");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Errore durante l'export Excel:", error);
        }
    };

    return (
        <div className="dettaglio-attivi-container">
            {isLoading ? (
                <Lf23Loader />
            ) : tableData.length === 0 ? (
                <div className="oper-hr-empty-state">Nessun dato disponibile</div>
            ) : (
                <>
                    <div className="dettaglio-attivi-cards">
                        {[
                            { tipo: 'det_to_indet', label: 'Da determinato a\nindeterminato nel', offset: 0 },
                            { tipo: 'tir_to_dip', label: 'Da tirocinio a\ndipendente nel', offset: 0 },
                            { tipo: 'det_to_indet', label: 'Da determinato a\nindeterminato nel', offset: 1 },
                            { tipo: 'tir_to_dip', label: 'Da tirocinio a\ndipendente nel', offset: 1 },
                        ].map((card, i) => {
                            const data = getCardData(card.tipo, card.offset);
                            return (
                                <div key={i} className="dettaglio-attivi-card">
                                    <div className="dettaglio-attivi-card-title">
                                        {card.label} {data.anno}:
                                    </div>
                                    <div className="dettaglio-attivi-card-value">{data.totale}</div>
                                    <div className="dettaglio-attivi-card-sub">
                                        di cui svantaggiati:
                                    </div>
                                    <div className="dettaglio-attivi-card-svan">{data.svantaggiati}</div>
                                    <div className="dettaglio-attivi-card-perc">
                                        Percentuale svantaggiati: {data.percentuale}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={`dettaglio-attivi-table-section${isFullscreenTable ? ' fullscreen-trend' : ''}`}>
                        <RotateDevice active={isFullscreenTable} />
                        <div className="dettaglio-attivi-table-header">
                            <button
                                className="dettaglio-attivi-download-btn"
                                onClick={handleExportExcel}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Esporta
                            </button>
                            <button
                                className="dettaglio-attivi-fullscreen-btn"
                                onClick={() => setIsFullscreenTable(!isFullscreenTable)}
                                title={isFullscreenTable ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenTable ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <div className="dettaglio-attivi-table-wrapper">
                            <table className="dettaglio-attivi-table">
                                <thead>
                                    <tr>
                                        {[
                                            { label: 'Operatore', key: 'operatore' },
                                            { label: 'codice_filiale', key: 'codice_filiale' },
                                            { label: 'Eta', key: 'eta' },
                                            { label: 'Sesso', key: 'sesso' },
                                            { label: 'Svantaggio', key: 'svantaggio' },
                                            { label: 'Data Inizio', key: 'data_inizio' },
                                            { label: 'Data Fine', key: 'data_fine' },
                                            { label: 'Tipologia Contratto', key: 'rapporto_lavoro' },
                                            { label: 'Dett Contratto', key: 'tipologia' },
                                            { label: 'Livello', key: 'livello' },
                                            { label: 'CCNL', key: 'ccnl' },
                                        ].map(({ label, key }) => (
                                            <th key={key} className="dettaglio-attivi-th-sortable" onClick={() => handleSort(key)}>
                                                {label}
                                                <span className="dettaglio-attivi-sort-icon">
                                                    {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedTableData.map((row, index) => (
                                        <tr key={index}>
                                            <td><span className="dettaglio-attivi-operatore-text">{row.operatore}</span></td>
                                            <td>{row.codice_filiale}</td>
                                            <td>{row.eta}</td>
                                            <td>{row.sesso}</td>
                                            <td>{row.svantaggio}</td>
                                            <td>{row.data_inizio}</td>
                                            <td>{row.data_fine}</td>
                                            <td>{row.rapporto_lavoro}</td>
                                            <td>{row.tipologia}</td>
                                            <td>{row.livello}</td>
                                            <td>{row.ccnl}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2} className="dettaglio-attivi-totale">
                                            Totale: {tableData.length}
                                        </td>
                                        <td colSpan={9}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};


export default DettaglioAttivi;